import { authAPI } from '@/utils/api'

export default {
  namespaced: true,
  
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('authToken') || null,
    isAuthenticated: !!localStorage.getItem('authToken'),
    loading: false,
    error: null
  },
  
  mutations: {
    SET_USER(state, user) {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
    SET_TOKEN(state, token) {
      state.token = token
      state.isAuthenticated = !!token
      if (token) {
        localStorage.setItem('authToken', token)
      } else {
        localStorage.removeItem('authToken')
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      localStorage.removeItem('user')
      localStorage.removeItem('authToken')
    }
  },
  
  // Helper function to map getCurrentUser response to our user data structure
  _mapGetCurrentUserResponse(serverUserData) {
    return {
      userId: serverUserData.userId,
      employeeId: serverUserData.employeeid, // Note: lowercase 'employeeid'
      username: serverUserData.userName,
      userOtp: serverUserData.userOtp,
      userStatus: serverUserData.userstatus,
      lang: serverUserData.lang,
      profileURL: serverUserData.profileURL,
      userType: serverUserData.accessType,
      // Additional fields from getCurrentUser
      userFullName: serverUserData.userFullName,
      // Prefer account alias; getCurrentUser often puts AliasName in userFullName
      aliasName: serverUserData.aliasName || serverUserData.userFullName || '',
      empCode: serverUserData.empCode || serverUserData.EmpCode || '',
      userDescription: serverUserData.userDescription,
      mail: serverUserData.mail,
      sex: serverUserData.sex,
      phone: serverUserData.phone,
      mobile: serverUserData.mobile,
      empFullNameEng: serverUserData.empFullNameEng,
      empFullNameLao: serverUserData.empFullNameLao,
      dateOfBirth: serverUserData.dateOfBirth,
      gender: serverUserData.gender,
      nationality: serverUserData.nationality,
      maritalStatus: serverUserData.maritalStatus,
      positionName: serverUserData.positionName,
      departmentName: serverUserData.departmentName,
      hireDate: serverUserData.hireDate,
      employmentStatus: serverUserData.employmentStatus,
      workLocation: serverUserData.workLocation,
      managerName: serverUserData.managerName,
      workSchedule: serverUserData.workSchedule,
      province: serverUserData.province,
      district: serverUserData.district,
      village: serverUserData.village,
      postalCode: serverUserData.postalCode,
      currentAddress: serverUserData.currentAddress,
      profilePicture: serverUserData.profilePicture
    }
  },
  
  actions: {
    async login({ commit }, credentials) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        const response = await authAPI.mobileLogin(credentials)
        
        console.log('Login response:', response)
        
        // Handle the mobile API response structure
        if (response && !response.DidError && response.access_token) {
          console.log('Processing login response:', response)
          
          // Determine the correct employee ID
          let employeeId = response.empId
          if (!employeeId || employeeId === 0) {
            employeeId = parseInt(response.username)
            console.log('Using username as employeeId:', employeeId)
          }
          
          const userData = {
            userId: response.userid ?? response.Userid,
            employeeId: employeeId,
            username: response.username ?? response.Username,
            aliasName: response.aliasName ?? response.AliasName ?? '',
            empCode: response.empCode ?? response.EmpCode ?? '',
            userOtp: response.userOtp,
            userStatus: response.userstatus,
            lang: response.lang,
            profileURL: response.profileURL,
            userType: response.userType,
            expiredDate: response.expiredDate,
            refreshExpiredDate: response.refreshExpiredDate,
            // Add additional fields that might be useful
            empId: response.empId ?? response.EmpId,
            userid: response.userid ?? response.Userid,
            access_token: response.access_token,
            refresh_token: response.refresh_token
          }
          
          console.log('Mapped user data:', userData)
          
          commit('SET_USER', userData)
          commit('SET_TOKEN', response.access_token)
          
          // Store main token in localStorage (what API client expects)
          localStorage.setItem('authToken', response.access_token)
          
          // Store refresh token for future use
          localStorage.setItem('refreshToken', response.refresh_token)
          
          // Store token in Flutter bridge if available
          if (window.flutterBridge && window.flutterBridge.isFlutter) {
            try {
              // Store in Flutter bridge with 'auth_token' key (what Flutter expects)
              await window.flutterBridge.setStorageValue('auth_token', response.access_token)
              console.log('✅ Token stored in Flutter bridge as auth_token')
            } catch (error) {
              console.error('❌ Failed to store token in Flutter bridge:', error)
            }
          } else {
            console.log('ℹ️ Not in Flutter app, token stored in localStorage as authToken')
          }
          
          return {
            didError: false,
            model: userData,
            access_token: response.access_token,
            refresh_token: response.refresh_token
          }
        } else {
          throw new Error(response?.ErrorMessage || 'Login failed')
        }
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // New method to ensure user profile data is loaded
    async ensureUserProfileData({ state, commit }) {
      try {
        const user = state.user
        if (!user || !user.employeeId) {
          console.log('❌ No user or employeeId available for profile data loading')
          return false
        }
        
        console.log('🔄 Ensuring user profile data is loaded for employeeId:', user.employeeId)
        
        // This will be called by other modules that need to ensure profile data is loaded
        // For now, just return true if we have the basic user data
        return true
      } catch (error) {
        console.error('❌ Error ensuring user profile data:', error)
        return false
      }
    },
    
    async logout({ commit, state }) {
      commit('SET_LOADING', true)
      
      try {
        const token = state.token || localStorage.getItem('authToken')
        if (token) {
          await authAPI.mobileLogout(token)
        }
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        commit('CLEAR_AUTH')
        localStorage.removeItem('refreshToken')
        commit('SET_LOADING', false)
      }
    },
    
    async getCurrentUser({ commit }) {
      commit('SET_LOADING', true)
      
      try {
        const response = await authAPI.getCurrentUser()
        if (response && !response.didError && response.model) {
          // Map the getCurrentUser response to our user data structure
          const serverUserData = response.model
          const mappedUserData = {
            userId: serverUserData.userId,
            employeeId: serverUserData.employeeid, // Note: lowercase 'employeeid'
            username: serverUserData.userName,
            userOtp: serverUserData.userOtp,
            userStatus: serverUserData.userstatus,
            lang: serverUserData.lang,
            profileURL: serverUserData.profileURL,
            userType: serverUserData.accessType,
            // Additional fields from getCurrentUser
            userFullName: serverUserData.userFullName,
            aliasName: serverUserData.aliasName || serverUserData.userFullName || '',
            empCode: serverUserData.empCode || serverUserData.EmpCode || '',
            userDescription: serverUserData.userDescription,
            mail: serverUserData.mail,
            sex: serverUserData.sex,
            phone: serverUserData.phone,
            mobile: serverUserData.mobile,
            empFullNameEng: serverUserData.empFullNameEng,
            empFullNameLao: serverUserData.empFullNameLao,
            dateOfBirth: serverUserData.dateOfBirth,
            gender: serverUserData.gender,
            nationality: serverUserData.nationality,
            maritalStatus: serverUserData.maritalStatus,
            positionName: serverUserData.positionName,
            departmentName: serverUserData.departmentName,
            hireDate: serverUserData.hireDate,
            employmentStatus: serverUserData.employmentStatus,
            workLocation: serverUserData.workLocation,
            managerName: serverUserData.managerName,
            workSchedule: serverUserData.workSchedule,
            province: serverUserData.province,
            district: serverUserData.district,
            village: serverUserData.village,
            postalCode: serverUserData.postalCode,
            currentAddress: serverUserData.currentAddress,
            profilePicture: serverUserData.profilePicture
          }
          
          console.log('🔄 Mapped getCurrentUser response:', mappedUserData)
          commit('SET_USER', mappedUserData)
        }
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async checkAuthStatus({ commit, state }) {
      try {
        const token = state.token || localStorage.getItem('authToken')
        if (token) {
          console.log('🔍 Checking auth status with token:', token ? 'Present' : 'Missing')
          
          // First, try to restore user data from localStorage
          const storedUser = localStorage.getItem('user')
          if (storedUser) {
            try {
              const userData = JSON.parse(storedUser)
              console.log('📦 Restored user data from localStorage:', userData)
              
              // Validate that we have essential user data
              if (userData && userData.employeeId && userData.username) {
                console.log('✅ Valid user data found in localStorage')
                commit('SET_USER', userData)
                
                // Optionally validate token with server (but don't fail if it doesn't work)
                try {
                  const response = await authAPI.getCurrentUser()
                  if (response && !response.didError && response.model) {
                    console.log('✅ Token validated with server, updating user data')
                    
                    // Map the getCurrentUser response to our user data structure
                    const mappedUserData = {
                      ...this._mapGetCurrentUserResponse(response.model),
                      // Keep existing fields from localStorage if not in server response
                      ...userData
                    }
                    
                    console.log('🔄 Mapped server user data:', mappedUserData)
                    commit('SET_USER', mappedUserData)
                  } else {
                    console.log('⚠️ Server validation failed, but keeping localStorage data')
                  }
                } catch (serverError) {
                  console.log('⚠️ Server validation error, but keeping localStorage data:', serverError.message)
                  // Don't clear auth on server error - keep localStorage data
                }
                
                return true
              } else {
                console.log('❌ Invalid user data in localStorage, missing employeeId or username')
                // Try server validation as fallback
                const response = await authAPI.getCurrentUser()
                if (response && !response.didError && response.model) {
                  console.log('✅ Server validation successful, updating user data')
                  
                  // Map the getCurrentUser response to our user data structure
                  const serverUserData = response.model
                  const mappedUserData = {
                    userId: serverUserData.userId,
                    employeeId: serverUserData.employeeid, // Note: lowercase 'employeeid'
                    username: serverUserData.userName,
                    userOtp: serverUserData.userOtp,
                    userStatus: serverUserData.userstatus,
                    lang: serverUserData.lang,
                    profileURL: serverUserData.profileURL,
                    userType: serverUserData.accessType,
                    // Additional fields from getCurrentUser
                    userFullName: serverUserData.userFullName,
                    aliasName: serverUserData.aliasName || serverUserData.userFullName || '',
                    empCode: serverUserData.empCode || serverUserData.EmpCode || '',
                    userDescription: serverUserData.userDescription,
                    mail: serverUserData.mail,
                    sex: serverUserData.sex,
                    phone: serverUserData.phone,
                    mobile: serverUserData.mobile,
                    empFullNameEng: serverUserData.empFullNameEng,
                    empFullNameLao: serverUserData.empFullNameLao,
                    dateOfBirth: serverUserData.dateOfBirth,
                    gender: serverUserData.gender,
                    nationality: serverUserData.nationality,
                    maritalStatus: serverUserData.maritalStatus,
                    positionName: serverUserData.positionName,
                    departmentName: serverUserData.departmentName,
                    hireDate: serverUserData.hireDate,
                    employmentStatus: serverUserData.employmentStatus,
                    workLocation: serverUserData.workLocation,
                    managerName: serverUserData.managerName,
                    workSchedule: serverUserData.workSchedule,
                    province: serverUserData.province,
                    district: serverUserData.district,
                    village: serverUserData.village,
                    postalCode: serverUserData.postalCode,
                    currentAddress: serverUserData.currentAddress,
                    profilePicture: serverUserData.profilePicture
                  }
                  
                  console.log('🔄 Mapped server user data (fallback):', mappedUserData)
                  commit('SET_USER', mappedUserData)
                  return true
                } else {
                  console.log('❌ Both localStorage and server validation failed')
                  commit('CLEAR_AUTH')
                  return false
                }
              }
            } catch (parseError) {
              console.error('❌ Error parsing stored user data:', parseError)
              // Try server validation as fallback
              const response = await authAPI.getCurrentUser()
              if (response && !response.didError && response.model) {
                console.log('✅ Server validation successful after localStorage parse error')
                
                // Map the getCurrentUser response to our user data structure
                const serverUserData = response.model
                const mappedUserData = {
                  userId: serverUserData.userId,
                  employeeId: serverUserData.employeeid, // Note: lowercase 'employeeid'
                  username: serverUserData.userName,
                  userOtp: serverUserData.userOtp,
                  userStatus: serverUserData.userstatus,
                  lang: serverUserData.lang,
                  profileURL: serverUserData.profileURL,
                  userType: serverUserData.accessType,
                  // Additional fields from getCurrentUser
                  userFullName: serverUserData.userFullName,
                  aliasName: serverUserData.aliasName || serverUserData.userFullName || '',
                  empCode: serverUserData.empCode || serverUserData.EmpCode || '',
                  userDescription: serverUserData.userDescription,
                  mail: serverUserData.mail,
                  sex: serverUserData.sex,
                  phone: serverUserData.phone,
                  mobile: serverUserData.mobile,
                  empFullNameEng: serverUserData.empFullNameEng,
                  empFullNameLao: serverUserData.empFullNameLao,
                  dateOfBirth: serverUserData.dateOfBirth,
                  gender: serverUserData.gender,
                  nationality: serverUserData.nationality,
                  maritalStatus: serverUserData.maritalStatus,
                  positionName: serverUserData.positionName,
                  departmentName: serverUserData.departmentName,
                  hireDate: serverUserData.hireDate,
                  employmentStatus: serverUserData.employmentStatus,
                  workLocation: serverUserData.workLocation,
                  managerName: serverUserData.managerName,
                  workSchedule: serverUserData.workSchedule,
                  province: serverUserData.province,
                  district: serverUserData.district,
                  village: serverUserData.village,
                  postalCode: serverUserData.postalCode,
                  currentAddress: serverUserData.currentAddress,
                  profilePicture: serverUserData.profilePicture
                }
                
                console.log('🔄 Mapped server user data (parse error fallback):', mappedUserData)
                commit('SET_USER', mappedUserData)
                return true
              } else {
                console.log('❌ Both localStorage parse and server validation failed')
                commit('CLEAR_AUTH')
                return false
              }
            }
          } else {
            console.log('📦 No stored user data found, trying server validation')
            // No stored user data, try server validation
            const response = await authAPI.getCurrentUser()
            if (response && !response.didError && response.model) {
              console.log('✅ Server validation successful')
              
              // Map the getCurrentUser response to our user data structure
              const serverUserData = response.model
              const mappedUserData = {
                userId: serverUserData.userId,
                employeeId: serverUserData.employeeid, // Note: lowercase 'employeeid'
                username: serverUserData.userName,
                userOtp: serverUserData.userOtp,
                userStatus: serverUserData.userstatus,
                lang: serverUserData.lang,
                profileURL: serverUserData.profileURL,
                userType: serverUserData.accessType,
                // Additional fields from getCurrentUser
                userFullName: serverUserData.userFullName,
                aliasName: serverUserData.aliasName || serverUserData.userFullName || '',
                empCode: serverUserData.empCode || serverUserData.EmpCode || '',
                userDescription: serverUserData.userDescription,
                mail: serverUserData.mail,
                sex: serverUserData.sex,
                phone: serverUserData.phone,
                mobile: serverUserData.mobile,
                empFullNameEng: serverUserData.empFullNameEng,
                empFullNameLao: serverUserData.empFullNameLao,
                dateOfBirth: serverUserData.dateOfBirth,
                gender: serverUserData.gender,
                nationality: serverUserData.nationality,
                maritalStatus: serverUserData.maritalStatus,
                positionName: serverUserData.positionName,
                departmentName: serverUserData.departmentName,
                hireDate: serverUserData.hireDate,
                employmentStatus: serverUserData.employmentStatus,
                workLocation: serverUserData.workLocation,
                managerName: serverUserData.managerName,
                workSchedule: serverUserData.workSchedule,
                province: serverUserData.province,
                district: serverUserData.district,
                village: serverUserData.village,
                postalCode: serverUserData.postalCode,
                currentAddress: serverUserData.currentAddress,
                profilePicture: serverUserData.profilePicture
              }
              
              console.log('🔄 Mapped server user data (no localStorage):', mappedUserData)
              commit('SET_USER', mappedUserData)
              return true
            } else {
              console.log('❌ Server validation failed')
              commit('CLEAR_AUTH')
              return false
            }
          }
        } else {
          console.log('❌ No token found')
          return false
        }
      } catch (error) {
        console.error('❌ Auth status check error:', error)
        // Only clear auth if it's a critical error
        if (error.response?.status === 401) {
          commit('CLEAR_AUTH')
        }
        return false
      }
    },
    
    clearError({ commit }) {
      commit('SET_ERROR', null)
    }
  },
  
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    user: state => state.user,
    token: state => state.token,
    loading: state => state.loading,
    error: state => state.error,
    userRole: state => state.user?.role || null,
    userId: state => state.user?.userId || null,
    employeeId: state => state.user?.employeeId || null,

    // Header/display label: AliasName, else EmpCode (never EmpId)
    displayName: state => {
      const u = state.user
      if (!u) return ''
      const alias = (u.aliasName || u.userFullName || '').toString().trim()
      if (alias) return alias
      const code = (u.empCode || '').toString().trim()
      if (code) return code
      return ''
    },

    // New getter to check if user data is complete and ready
    isUserDataComplete: state => {
      const user = state.user
      return user &&
             user.employeeId &&
             user.username &&
             user.userId &&
             !isNaN(user.employeeId) &&
             user.employeeId > 0
    },

    // Getter to check if user data is ready for profile operations
    isProfileDataReady: state => {
      const user = state.user
      return user &&
             user.employeeId &&
             !isNaN(user.employeeId) &&
             user.employeeId > 0
    }
  }
}